import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PORTAL_TOOLTIP_Z, getTooltipPortalRoot } from "../../utils/panelZIndex";

const MOBILE_TOOLTIP_MEDIA_QUERY = "(hover: none), (pointer: coarse)";
const MOBILE_TOOLTIP_AUTO_DISMISS_MS = 1000;

const Tooltip = ({
  content,
  children,
  side = "top",
  sideOffset = 6,
  fullWidth = false,
  delayMs = 0,
}) => {
  const hasContent = Boolean(content);
  const triggerClass = fullWidth ? "flex w-full min-w-0" : "inline-flex";
  const innerClass = fullWidth ? "flex w-full min-w-0" : "inline-flex";

  const triggerRef = useRef(null);
  const openTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (side === "bottom") {
      setPosition({
        left: rect.left + rect.width / 2,
        top: rect.bottom + sideOffset,
      });
      return;
    }
    if (side === "left") {
      setPosition({
        left: rect.left - sideOffset,
        top: rect.top + rect.height / 2,
      });
      return;
    }
    if (side === "right") {
      setPosition({
        left: rect.right + sideOffset,
        top: rect.top + rect.height / 2,
      });
      return;
    }
    setPosition({
      left: rect.left + rect.width / 2,
      top: rect.top - sideOffset,
    });
  }, [side, sideOffset]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  const setTooltipOpen = useCallback((nextOpen) => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }

    setOpen(nextOpen);

    if (
      nextOpen &&
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_TOOLTIP_MEDIA_QUERY).matches
    ) {
      dismissTimerRef.current = window.setTimeout(() => {
        setOpen(false);
        dismissTimerRef.current = null;
      }, MOBILE_TOOLTIP_AUTO_DISMISS_MS);
    }
  }, []);

  const requestTooltipOpen = useCallback(() => {
    if (delayMs <= 0) {
      setTooltipOpen(true);
      return;
    }
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
    }
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setTooltipOpen(true);
    }, delayMs);
  }, [delayMs, setTooltipOpen]);

  React.useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
      }
      if (dismissTimerRef.current) {
        window.clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  const transform =
    side === "bottom"
      ? "translate(-50%, 0)"
      : side === "left"
        ? "translate(-100%, -50%)"
        : side === "right"
          ? "translate(0, -50%)"
          : "translate(-50%, -100%)";

  if (!hasContent) {
    return <>{children}</>;
  }

  return (
    <span
      ref={triggerRef}
      className={triggerClass}
      onMouseEnter={requestTooltipOpen}
      onMouseLeave={() => setTooltipOpen(false)}
      onFocus={requestTooltipOpen}
      onBlur={() => setTooltipOpen(false)}
    >
      <span className={innerClass}>{children}</span>
      {open &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed max-w-xs rounded-md border border-gray-700 bg-gray-900 px-2 py-1.5 text-xs text-gray-100 shadow-lg"
            style={{
              left: `${position.left}px`,
              top: `${position.top}px`,
              transform,
              zIndex: PORTAL_TOOLTIP_Z,
            }}
          >
            {content}
          </span>,
          getTooltipPortalRoot() ?? document.body
        )}
    </span>
  );
};

export default Tooltip;
